import type { Toast } from 'types/common';
import { createWithEqualityFn } from 'zustand/traditional';

const getInitialBrowserPathname = () =>
  typeof globalThis.window === 'undefined'
    ? ''
    : globalThis.window.location.pathname;

export interface ShellStore {
  artifactViewerProps: {
    artifactId?: string;
    isOpen: boolean;
    title?: string;
  };
  browserPathname: string;
  isArtifactViewerOpen: boolean;
  isOffline: boolean;
  isQuestionPanelOpen: boolean;
  isRightPanelOpen: boolean;
  isSelectedAudienceTooltipOpen: boolean;
  isSidebarExpanded: boolean;
  isSourceInfoDialogOpen: boolean;
  pathname: string;
  rightPanelProps: {
    content?: string;
    title?: string;
    isOpen: boolean;
    onToggleCallback: (() => void) | null;
  };
  toast: Toast;
  setArtifactViewerProps: (props: ShellStore['artifactViewerProps']) => void;
  setIsArtifactViewerOpen: (open: boolean) => void;
  setIsOffline: (isOffline: boolean) => void;
  setIsQuestionPanelOpen: (open: boolean) => void;
  setIsSidebarExpanded: (expanded: boolean) => void;
  setIsRightPanelOpen: (open: boolean) => void;
  setIsSourceInfoDialogOpen: (open: boolean) => void;
  setIsSelectedAudienceTooltipOpen: (open: boolean) => void;
  setBrowserPathname: (pathname: string) => void;
  setPathname: (pathname: string) => void;
  setRightPanelProps: (props: ShellStore['rightPanelProps']) => void;
  setToast: (toast: Toast) => void;
  reset: () => void;
}

export const useShellStore = createWithEqualityFn<ShellStore>((set, get) => ({
  artifactViewerProps: {
    isOpen: false
  },
  browserPathname: getInitialBrowserPathname(),
  isArtifactViewerOpen: false,
  isOffline: false,
  isQuestionPanelOpen: false,
  isRightPanelOpen: false,
  isSidebarExpanded: true,
  isSelectedAudienceTooltipOpen: true,
  isSourceInfoDialogOpen: false,
  pathname: '',
  rightPanelProps: {
    isOpen: false,
    onToggleCallback: null
  },
  toast: { isOpen: false, message: '' },
  setArtifactViewerProps: (props) =>
    set({
      artifactViewerProps: props,
      isArtifactViewerOpen: props.isOpen,
      isRightPanelOpen: props.isOpen ? false : get().isRightPanelOpen,
      rightPanelProps: props.isOpen
        ? { ...get().rightPanelProps, isOpen: false }
        : get().rightPanelProps
    }),
  setBrowserPathname: (browserPathname) => set({ browserPathname }),
  setPathname: (pathname) => set({ pathname }),
  setIsArtifactViewerOpen: (open) =>
    set({
      isArtifactViewerOpen: open,
      artifactViewerProps: { ...get().artifactViewerProps, isOpen: open },
      isRightPanelOpen: open ? false : get().isRightPanelOpen,
      rightPanelProps: open
        ? { ...get().rightPanelProps, isOpen: false }
        : get().rightPanelProps
    }),
  setIsOffline: (isOffline) => set({ isOffline }),
  setIsQuestionPanelOpen: (open) => set({ isQuestionPanelOpen: open }),
  setIsSidebarExpanded: (expanded) => set({ isSidebarExpanded: expanded }),
  setIsRightPanelOpen: (open) => {
    set({
      isRightPanelOpen: open,
      isArtifactViewerOpen: open ? false : get().isArtifactViewerOpen,
      artifactViewerProps: open
        ? { ...get().artifactViewerProps, isOpen: false }
        : get().artifactViewerProps,
      rightPanelProps: { ...get().rightPanelProps, isOpen: open }
    });
    get().rightPanelProps.onToggleCallback?.();
  },
  setToast: (toast) =>
    set({
      toast: {
        isOpen: Boolean(toast.message),
        ...toast,
        position: toast.position || 'absolute',
        timeout: toast.timeout || 2000
      }
    }),
  setIsSourceInfoDialogOpen: (open) => set({ isSourceInfoDialogOpen: open }),
  setIsSelectedAudienceTooltipOpen: (open) =>
    set({ isSelectedAudienceTooltipOpen: open }),
  setRightPanelProps: (props) =>
    set({
      rightPanelProps: props,
      isRightPanelOpen: props.isOpen,
      isArtifactViewerOpen: props.isOpen ? false : get().isArtifactViewerOpen,
      artifactViewerProps: props.isOpen
        ? { ...get().artifactViewerProps, isOpen: false }
        : get().artifactViewerProps
    }),
  reset: () =>
    set(() => ({
      artifactViewerProps: {
        isOpen: false
      },
      isArtifactViewerOpen: false,
      isQuestionPanelOpen: false,
      isRightPanelOpen: false,
      rightPanelProps: {
        isOpen: false,
        onToggleCallback: null
      }
    }))
}));
